import React, { useState } from 'react';
import PropTypes from 'prop-types';

import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import Checkbox from 'core/components/checkbox';
import backArrow from 'core/assets/svg/ic-back-arrow.svg';
import infoIcon from 'core/assets/svg/ic-info-black.svg';
import { dateFormatter } from 'core/helpers/formatters';
import { getIncidentTypeLabel } from 'customer/components/policy/incidents/helpers';
import { countLabel, describeIncident } from './incident-labels';
import useStyles from './ignore-incidents.styles';

export const IgnoreDialogMode = { Ignore: 'ignore', StopIgnoring: 'stopIgnoring' };

const copy = {
  [IgnoreDialogMode.Ignore]: {
    title: ['Ignore this incident?', 'Ignore {n} incidents?'],
    body: () =>
      "Ignored incidents stay on the record but don't affect price, eligibility, or underwriting review from now on. If one already turned off auto or excluded a driver, ignoring it won't undo that. Override the error or include the driver again.",
    alsoTitle: ['Also on this date', 'Also on these dates'],
    alsoBody: (everyTieredViolationCounts) =>
      everyTieredViolationCounts
        ? "They're on the same date, so they're likely from the same event. Uncheck any that should still count."
        : "They're on the same date, so they're likely from the same event. Uncheck any that should still count. Only the most severe one on a date counts, so one you uncheck may count instead."
  },
  [IgnoreDialogMode.StopIgnoring]: {
    title: ['Stop ignoring this incident?', 'Stop ignoring {n} incidents?'],
    body: (count) =>
      count === 1
        ? "It'll count toward price and eligibility again."
        : "They'll count toward price and eligibility again.",
    alsoTitle: ['Also ignored on this date', 'Also ignored on these dates'],
    alsoBody: () => 'Uncheck any that should stay ignored.'
  }
};

const IncidentRecord = ({ incident, classes }) => (
  <div className={classes.record}>
    <div className={classes.recordDivider} />
    <div className={classes.recordInfo}>
      <span className={classes.recordName}>{`Violation (${getIncidentTypeLabel(incident.type)})`}</span>
      <div className={classes.recordMeta}>
        <span>{dateFormatter(incident.date)}</span>
        {incident.driver && (
          <>
            <span>&bull;</span>
            <span>{incident.driver}</span>
          </>
        )}
      </div>
    </div>
  </div>
);

IncidentRecord.propTypes = {
  incident: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired
};

// Laid out like the offer's claim dispute modals. The other incidents on the same dates start checked:
// codes on one date almost always come from the same event, so the usual choice is all of them.
const IgnoreIncidentsDialog = ({
  mode,
  incidents,
  otherIncidentsOnDates,
  everyTieredViolationCounts,
  backLabel,
  homeOption,
  defaultIncludeHome,
  confirmLabel,
  loading,
  error,
  onConfirm,
  onClose
}) => {
  const classes = useStyles();
  const [alsoSelected, setAlsoSelected] = useState(() =>
    Object.fromEntries(otherIncidentsOnDates.map((incident) => [incident.id, incident]))
  );
  const [includeHome, setIncludeHome] = useState(defaultIncludeHome);
  const { title, body, alsoTitle, alsoBody } = copy[mode];
  const count = incidents.length + Object.keys(alsoSelected).length;
  const dateCount = new Set(otherIncidentsOnDates.map((incident) => incident.date)).size;

  const toggleAlso = (incident, checked) =>
    setAlsoSelected(({ [incident.id]: _removed, ...others }) =>
      checked ? { ...others, [incident.id]: incident } : others
    );

  return (
    <BaseDialog size="xs" open onClose={loading ? undefined : onClose} blockContent className={classes.paper}>
      <div className={classes.content}>
        <div className={classes.textGroup}>
          {backLabel && (
            <button type="button" className={classes.backButton} disabled={loading} onClick={onClose}>
              <img src={backArrow} alt="" className={classes.backIcon} />
              {backLabel}
            </button>
          )}
          <Label type="greenBig">{countLabel(count, title[0], title[1])}</Label>
          <p className={classes.body}>{body(count)}</p>
        </div>

        <div className={classes.records}>
          {incidents.map((incident) => (
            <IncidentRecord key={incident.id} incident={incident} classes={classes} />
          ))}
        </div>

        {otherIncidentsOnDates.length > 0 && (
          <section className={classes.alsoSection} aria-labelledby="ignore-incidents-also-title">
            <p id="ignore-incidents-also-title" className={classes.fieldLabel}>
              {countLabel(dateCount, alsoTitle[0], alsoTitle[1])}
            </p>
            <p className={classes.alsoBody}>{alsoBody(everyTieredViolationCounts)}</p>
            {otherIncidentsOnDates.map((incident) => (
              <Checkbox
                key={incident.id}
                mode="light"
                id={`ignore-also-${incident.id}`}
                label={describeIncident(incident)}
                value={incident.id in alsoSelected}
                onChange={({ target: { checked } }) => toggleAlso(incident, checked)}
              />
            ))}
          </section>
        )}

        {homeOption && (
          <div className={classes.callout}>
            <img src={infoIcon} alt="" className={classes.calloutIcon} />
            <div>
              <p className={classes.calloutText}>Some auto claims and violations also affect the home price.</p>
              <Checkbox
                mode="light"
                id="ignore-for-home"
                label="Ignore for the home price too"
                value={includeHome}
                onChange={({ target: { checked } }) => setIncludeHome(checked)}
              />
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className={classes.error}>
            {error}
          </p>
        )}

        <div className={classes.actions}>
          <Button variant="contained" color="secondary" disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            loading={loading}
            disabled={loading}
            onClick={() =>
              onConfirm([...incidents, ...Object.values(alsoSelected)], { includeHome: homeOption && includeHome })
            }
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

IgnoreIncidentsDialog.propTypes = {
  mode: PropTypes.oneOf(Object.values(IgnoreDialogMode)).isRequired,
  incidents: PropTypes.array.isRequired,
  // the other incidents on the same drivers' dates; they start checked
  otherIncidentsOnDates: PropTypes.array,
  // the brand's countsEveryTieredAutoViolation flag (CFM)
  everyTieredViolationCounts: PropTypes.bool,
  // a link back to the screen the dialog opened from
  backLabel: PropTypes.string,
  // a bundled offer can ignore the home copies in the same save
  homeOption: PropTypes.bool,
  // whether the home option starts checked; a retry keeps the underwriter's choice
  defaultIncludeHome: PropTypes.bool,
  confirmLabel: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

IgnoreIncidentsDialog.defaultProps = {
  otherIncidentsOnDates: [],
  everyTieredViolationCounts: false,
  backLabel: null,
  homeOption: false,
  defaultIncludeHome: true,
  loading: false,
  error: null
};

export default IgnoreIncidentsDialog;
